import type { SupabaseClient } from "@supabase/supabase-js";
import { STATUS_IDS } from "@/lib/constants/statuses";
import { BOOKING_TYPE_IDS } from "@/lib/constants/booking-types";

/**
 * The 15-minute rule: a studio needs this long between two shows for prep and
 * cleanup. Staff cannot book past it - the booking pages stop them, and the
 * staff booking routes refuse it.
 *
 * Times are compared as stored in show_session_timeslot: the venue's wall
 * clock written as UTC ("2025-07-20T09:15:00+00:00"), so UTC fields are read
 * throughout and the server's own time zone never matters.
 */
export const GAP_MINUTES = 15;

/** Bookings in these statuses hold their studio. */
const HOLDING_STATUS_IDS = [
  STATUS_IDS.BOOKING_CONFIRMED,
  STATUS_IDS.BOOKING_PRELIMINARY,
  STATUS_IDS.BOOKING_DRAFT,
];

export type GapConflict = {
  studioId: number;
  showName: string;
  /** YYYY-MM-DD */
  date: string;
  /** HH:MM */
  startTime: string;
  /** HH:MM */
  endTime: string;
  /** Minutes between the two shows; 0 when they overlap. */
  timeGap: number;
};

export type GapCheck = {
  venueId: number;
  /** YYYY-MM-DD */
  showDate: string;
  /** HH:MM */
  startTime: string;
  /** Minutes. */
  duration: number;
  /**
   * Studios the show could run in. The check fails only when every one of
   * them has a show too close; null checks the whole venue.
   */
  studioIds: number[] | null;
  /** The booking being edited, whose own shows don't count. */
  excludeBookingId?: number;
};

const minutesOf = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

const hhmm = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

/**
 * A session time as its wall clock, read as UTC. show_session_timeslot is a
 * `timestamp without time zone`, so Postgres keeps the time as written and
 * drops any offset: the staff page sends "2026-10-13T12:30:00+02:00" and
 * 12:30 is stored. The offset is ignored here too, or the check would look
 * at a different time than the one booked.
 */
export const parseSession = (value: string) =>
  new Date(`${value.replace(/(Z|[+-]\d{2}:?\d{2})$/i, "").replace(" ", "T")}Z`);

const nextDay = (date: string) => {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

/**
 * The first show too close to the one being booked, or null when it fits.
 * With several studios, null as soon as one of them is clear.
 */
export async function findGapConflict(
  supabase: SupabaseClient,
  check: GapCheck,
): Promise<GapConflict | null> {
  const conflicts = await gapConflictsByStudio(supabase, check);
  if (check.studioIds && check.studioIds.length > 0) {
    const blocked = check.studioIds.every((id) => conflicts.has(id));
    return blocked ? (conflicts.get(check.studioIds[0]) ?? null) : null;
  }
  return conflicts.values().next().value ?? null;
}

/** For each studio with a show too close, the first such show. */
export async function gapConflictsByStudio(
  supabase: SupabaseClient,
  check: GapCheck,
): Promise<Map<number, GapConflict>> {
  const {
    venueId,
    showDate,
    startTime,
    duration,
    studioIds,
    excludeBookingId,
  } = check;

  let query = supabase
    .from("studio_show_host_bookings")
    .select(
      `booking_id, studio_id, show_session_timeslot, custom_duration_minutes,
      bookings!inner(venue_id, status_id),
      show_time_slot_pricing(shows(duration_minutes, show_infos(name, lang)))`,
    )
    .eq("is_archived", false)
    // Road shows run off-site and never occupy a venue studio.
    .eq("booking_type_id", BOOKING_TYPE_IDS.ON_SITE)
    .eq("bookings.venue_id", venueId)
    .in("bookings.status_id", HOLDING_STATUS_IDS)
    .gte("show_session_timeslot", showDate)
    .lt("show_session_timeslot", nextDay(showDate));
  if (studioIds && studioIds.length > 0)
    query = query.in("studio_id", studioIds);
  if (excludeBookingId) query = query.neq("booking_id", excludeBookingId);

  const { data, error } = await query;
  if (error)
    throw new Error(`Could not check the gap between shows: ${error.message}`);

  const newStart = minutesOf(startTime);
  const newEnd = newStart + duration;
  const conflicts = new Map<number, GapConflict>();

  for (const row of data ?? []) {
    if (conflicts.has(row.studio_id)) continue;

    const at = parseSession(row.show_session_timeslot);
    if (at.toISOString().slice(0, 10) !== showDate) continue;

    const pricing = row.show_time_slot_pricing as unknown as {
      shows: {
        duration_minutes: number | null;
        show_infos: { name: string; lang: string }[] | null;
      } | null;
    } | null;
    const show = pricing?.shows;
    const start = at.getUTCHours() * 60 + at.getUTCMinutes();
    const end =
      start + (row.custom_duration_minutes || show?.duration_minutes || 60);

    // Overlap counts as no gap at all.
    const gap =
      newEnd <= start ? start - newEnd : newStart >= end ? newStart - end : 0;
    if (gap >= GAP_MINUTES) continue;

    conflicts.set(row.studio_id, {
      studioId: row.studio_id,
      showName:
        show?.show_infos?.find((info) => info.lang === "en")?.name ||
        show?.show_infos?.[0]?.name ||
        "another show",
      date: showDate,
      startTime: hhmm(start),
      endTime: hhmm(end),
      timeGap: gap,
    });
  }

  return conflicts;
}

/** The staff-facing reason a booking was refused. */
export const gapConflictMessage = (conflict: GapConflict) =>
  conflict.timeGap === 0
    ? `This overlaps "${conflict.showName}" (${conflict.startTime}–${conflict.endTime}) in the same studio. Shows need at least ${GAP_MINUTES} minutes between them.`
    : `Only ${conflict.timeGap} minutes between this and "${conflict.showName}" (${conflict.startTime}–${conflict.endTime}) in the same studio. Shows need at least ${GAP_MINUTES} minutes between them.`;

export type SessionToCheck = {
  /** show_session_timeslot as sent or stored. */
  at: string;
  studioId: number;
  /** Minutes; looked up from the pricing slot or show when not given. */
  duration?: number | null;
  pricingId?: number | null;
  showId?: number | null;
};

/**
 * The first session of a booking that breaks the 15-minute rule, or null.
 * Use a client that can see every booking at the venue (staff, or the
 * service role for customer requests); a customer's own client only sees
 * their bookings and would let anything through.
 */
export async function findSessionsGapConflict(
  supabase: SupabaseClient,
  venueId: number,
  sessions: SessionToCheck[],
  excludeBookingId?: number,
): Promise<GapConflict | null> {
  const toCheck = sessions.filter((s) => s.at && s.studioId);
  if (toCheck.length === 0) return null;

  const pricingIds = [
    ...new Set(
      toCheck
        .filter((s) => !s.duration && s.pricingId)
        .map((s) => s.pricingId!),
    ),
  ];
  const showIds = [
    ...new Set(
      toCheck
        .filter((s) => !s.duration && !s.pricingId && s.showId)
        .map((s) => s.showId!),
    ),
  ];
  const byPricing = new Map<number, number>();
  const byShow = new Map<number, number>();
  if (pricingIds.length) {
    const { data } = await supabase
      .from("show_time_slot_pricing")
      .select("id, shows(duration_minutes)")
      .in("id", pricingIds);
    for (const row of (data ?? []) as any[]) {
      const show = Array.isArray(row.shows) ? row.shows[0] : row.shows;
      if (show?.duration_minutes) byPricing.set(row.id, show.duration_minutes);
    }
  }
  if (showIds.length) {
    const { data } = await supabase
      .from("shows")
      .select("id, duration_minutes")
      .in("id", showIds);
    for (const row of data ?? []) {
      if (row.duration_minutes) byShow.set(row.id, row.duration_minutes);
    }
  }

  for (const session of toCheck) {
    const at = parseSession(session.at).toISOString();
    const conflict = await findGapConflict(supabase, {
      venueId,
      showDate: at.slice(0, 10),
      startTime: at.slice(11, 16),
      duration:
        session.duration ||
        (session.pricingId ? byPricing.get(session.pricingId) : undefined) ||
        (session.showId ? byShow.get(session.showId) : undefined) ||
        60,
      studioIds: [session.studioId],
      excludeBookingId,
    });
    if (conflict) return conflict;
  }
  return null;
}
